import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, addDoc, setDoc, onSnapshot, doc, getDoc, serverTimestamp } from 'firebase/firestore';

const RPE_LABELS = {
  0: 'Ruhe', 1: 'Sehr leicht', 2: 'Leicht', 3: 'Mäßig',
  4: 'Etwas anstrengend', 5: 'Anstrengend', 6: 'Anstrengend+',
  7: 'Sehr anstrengend', 8: 'Sehr anstrengend+',
  9: 'Sehr sehr anstrengend', 10: 'Mein härtester Fight'
};

const WEEKS = Array.from({ length: 48 }, (_, i) => `Woche ${i + 1}`);
const DAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const SESSION_TYPES = ['Training', 'Spiel', 'Reha', 'Individuelles Training'];
// Individuelles Training is intentionally excluded — Martin only bills
// clubs for Training, Spiel, and Reha sessions, so only those should
// count toward attendance.
const ATTENDANCE_SESSION_TYPES = ['Training', 'Spiel', 'Reha'];
const WELLNESS = ['Gut geschlafen', 'Schlecht geschlafen', 'Sehr erholt', 'Müde', 'Sehr müde'];

export default function PlayerForm() {
  const { clubId } = useParams();
  const [clubName, setClubName] = useState('');
  const [clubLogoUrl, setClubLogoUrl] = useState('');
  const [players, setPlayers] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const [form, setForm] = useState({
    playerId: '',
    playerName: '',
    sessionType: '',
    duration: '',
    rpe: null,
    week: '',
    day: '',
    wellness: ''
  });

  useEffect(() => {
    if (!clubId) return;
    // Load club info
    getDoc(doc(db, 'clubs', clubId)).then(d => {
      if (d.exists()) {
        setClubName(d.data().name);
        setClubLogoUrl(d.data().logoUrl || '');
      }
      else setNotFound(true);
    });
    // Load players
    const unsub = onSnapshot(collection(db, 'clubs', clubId, 'players'), snap => {
      setPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => a.name.localeCompare(b.name)));
    });
    return unsub;
  }, [clubId]);

  function setField(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit() {
    const { playerId, playerName, sessionType, duration, rpe, week, day, wellness } = form;
    if (!playerId || !sessionType || !duration || rpe === null || !week || !day || !wellness) {
      alert('Bitte alle Felder ausfüllen!');
      return;
    }
    setLoading(true);

    // The session itself is the critical write — if this fails, the player
    // needs to know and retry.
    try {
      const load = parseInt(duration) * rpe;
      await addDoc(collection(db, 'sessions'), {
        clubId,
        playerId,
        playerName,
        sessionType,
        duration: parseInt(duration),
        rpe,
        load,
        week,
        day,
        wellness,
        timestamp: serverTimestamp()
      });
    } catch (err) {
      alert('Fehler beim Speichern. Bitte nochmal versuchen.');
      setLoading(false);
      return;
    }

    // Attendance is a secondary, best-effort write. Only Training, Spiel,
    // and Reha count toward attendance — Individuelles Training is
    // excluded since it's not billed to the club.
    if (ATTENDANCE_SESSION_TYPES.includes(sessionType)) {
      try {
        const now = new Date();
        const attMonth = now.getMonth();
        const attDay = now.getDate();
        const attKey = `${clubId}_${playerId}_${attMonth}_${attDay}`;
        await setDoc(doc(db, 'attendance', attKey), {
          key: attKey,
          clubId,
          playerId,
          playerName,
          month: attMonth,
          day: attDay,
          present: true,
          createdAt: serverTimestamp()
        }, { merge: true });
      } catch (err) {
        console.error('Attendance write failed (session was still saved):', err);
      }
    }

    setSubmitted(true);
    setLoading(false);
  }

  if (notFound) return (
    <div className="player-form-page">
      <div className="player-form-card" style={{ textAlign: 'center' }}>
        <h2 style={{ color: '#C0392B' }}>Verein nicht gefunden</h2>
        <p style={{ color: '#888', marginTop: 8 }}>Bitte den Link prüfen.</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="player-form-page">
      <div className="player-form-card" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <h2 style={{ color: '#1E8449', marginBottom: 8 }}>Danke!</h2>
        <p style={{ color: '#666', marginBottom: 24 }}>
          Deine Einheit wurde erfolgreich gespeichert.
        </p>
        <p style={{ fontSize: 13, color: '#888', marginBottom: 24 }}>
          {form.playerName} — {form.sessionType} — {form.duration} Min — RPE {form.rpe} — {parseInt(form.duration) * form.rpe} AU
        </p>
        <button className="btn-primary" style={{ width: '100%' }} onClick={() => {
          setSubmitted(false);
          setForm({ playerId: '', playerName: '', sessionType: '', duration: '', rpe: null, week: '', day: '', wellness: '' });
        }}>
          Neue Einheit eintragen
        </button>
      </div>
    </div>
  );

  return (
    <div className="player-form-page">
      <div className="player-form-card">
        {/* Header */}
        <div className="player-form-header">
          {clubLogoUrl && (
            <img
              src={clubLogoUrl}
              alt={clubName}
              style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 8, marginBottom: 8 }}
            />
          )}
          <h2>RPE – Subjektives Belastungsempfinden</h2>
          {clubName && <p style={{ fontSize: 13, marginTop: 4, opacity: 0.9 }}>{clubName}</p>}
        </div>

        {/* Spieler */}
        <div className="form-group">
          <label>Wer bist du? *</label>
          <select
            value={form.playerId}
            onChange={e => {
              const selected = players.find(p => p.id === e.target.value);
              setForm(prev => ({
                ...prev,
                playerId: e.target.value,
                playerName: selected ? selected.name : ''
              }));
            }}
          >
            <option value="">Spieler auswählen...</option>
            {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {/* Art der Einheit */}
        <div className="form-group">
          <label>Art der Einheit *</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {SESSION_TYPES.map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setField('sessionType', t)}
                style={{
                  padding: '12px',
                  border: '2px solid',
                  borderColor: form.sessionType === t ? '#C0392B' : '#DDD',
                  borderRadius: 6,
                  background: form.sessionType === t ? '#C0392B' : '#FFF',
                  color: form.sessionType === t ? '#FFF' : '#333',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 'bold'
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Dauer */}
        <div className="form-group">
          <label>Dauer der Einheit (Minuten) *</label>
          <input
            type="number"
            value={form.duration}
            onChange={e => setField('duration', e.target.value)}
            placeholder="z.B. 90"
            min="1"
            max="300"
          />
        </div>

        {/* RPE Scale */}
        <div className="form-group">
          <label>Wie anstrengend war die Einheit? (RPE 0–10) *</label>
          <div className="rpe-scale">
            {Array.from({ length: 11 }, (_, i) => (
              <div key={i}>
                <button
                  type="button"
                  className={`rpe-btn ${form.rpe === i ? 'selected' : ''}`}
                  onClick={() => setField('rpe', i)}
                >
                  {i}
                </button>
                <div className="rpe-label">{i === 0 ? 'Ruhe' : i === 10 ? 'Max' : ''}</div>
              </div>
            ))}
          </div>
          {form.rpe !== null && (
            <p style={{ fontSize: 13, color: '#C0392B', marginTop: 8, textAlign: 'center', fontWeight: 'bold' }}>
              {form.rpe} — {RPE_LABELS[form.rpe]}
            </p>
          )}
        </div>

        {/* Woche */}
        <div className="form-group">
          <label>Welche Woche? *</label>
          <select value={form.week} onChange={e => setField('week', e.target.value)}>
            <option value="">Woche auswählen...</option>
            {WEEKS.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>

        {/* Tag */}
        <div className="form-group">
          <label>Welcher Tag? *</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
            {DAYS.map(d => (
              <button
                key={d}
                type="button"
                onClick={() => setField('day', d)}
                style={{
                  padding: '8px 4px',
                  border: '2px solid',
                  borderColor: form.day === d ? '#C0392B' : '#DDD',
                  borderRadius: 6,
                  background: form.day === d ? '#C0392B' : '#FFF',
                  color: form.day === d ? '#FFF' : '#333',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 'bold'
                }}
              >
                {d.substring(0, 2)}
              </button>
            ))}
          </div>
          {form.day && <p style={{ fontSize: 12, color: '#888', marginTop: 6, textAlign: 'center' }}>{form.day}</p>}
        </div>

        {/* Wohlbefinden */}
        <div className="form-group">
          <label>Wie fühlst du dich heute? *</label>
          {WELLNESS.map(w => (
            <button
              key={w}
              type="button"
              onClick={() => setField('wellness', w)}
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 14px',
                marginBottom: 6,
                border: '2px solid',
                borderColor: form.wellness === w ? '#C0392B' : '#DDD',
                borderRadius: 6,
                background: form.wellness === w ? '#C0392B' : '#FFF',
                color: form.wellness === w ? '#FFF' : '#333',
                cursor: 'pointer',
                fontSize: 14,
                textAlign: 'left',
                fontWeight: form.wellness === w ? 'bold' : 'normal'
              }}
            >
              {w}
            </button>
          ))}
        </div>

        {/* Load preview */}
        {form.duration && form.rpe !== null && (
          <div style={{
            background: '#F5F5F5',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: 13, color: '#666' }}>Belastung (AU):</span>
            <span style={{
              fontSize: 20,
              fontWeight: 'bold',
              color: parseInt(form.duration) * form.rpe > 900 ? '#C0392B' :
                     parseInt(form.duration) * form.rpe > 700 ? '#7D6608' : '#1E8449'
            }}>
              {parseInt(form.duration) * form.rpe}
            </span>
          </div>
        )}

        {/* Submit */}
        <button
          className="btn-primary"
          style={{ width: '100%', padding: 16, fontSize: 16 }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Wird gespeichert...' : 'Einheit speichern ✓'}
        </button>

        <p style={{ textAlign: 'center', fontSize: 11, color: '#CCC', marginTop: 16 }}>
          RPE Monitoring System
        </p>
      </div>
    </div>
  );
}